import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import "moment/locale/ar"; // استيراد اللغة العربية
import Layout from "../components/Layout";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  message,
} from "antd";
import {
  Plus,
  Clock,
  Filter,
  Eye,
  Trash2,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { sessionsService } from "../services/api.service";
import AddSessionModal from "../components/sessions/AddSessionModal";
import ViewSessionModel from "../components/sessions/ViewSessionModel";
import EditSessionModal from "../components/sessions/EditSessionModal";
import SessionNotifications from "../components/notifications/SessionNotifications";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import "../styles/calendar.css";
import logger from "../utils/logger";

const localizer = momentLocalizer(moment);
moment.locale("ar  ");

const { Option } = Select;

const eventTypes = [
  { value: "court", label: "جلسة قضائية", color: "#3b82f6" },
  { value: "appeal", label: "استئناف", color: "#eab308" },
  { value: "consultation", label: "استشارة", color: "#22c55e" },
  { value: "other", label: "أخرى", color: "#64748b" },
];

const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewSessionModalVisible, setViewSessionModalVisible] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState(null);
  const [currentView, setCurrentView] = useState("month");

  // جلب الجلسات والمواعيد
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await sessionsService.getAllSessions();

      const formattedEvents = response.map((session) => {
        // إنشاء التاريخ باستخدام moment.utc() مع set للتوقيت
        const dateTime = moment(session.date)
          .set({
            hour: parseInt(session.time.split(":")[0]),
            minute: parseInt(session.time.split(":")[1]),
            second: parseInt(session.time.split(":")[2] || 0),
          })
          .local() // تحويل إلى التوقيت المحلي
          .toDate();

        // للتأكد من صحة التواريخ
        // console.log('Original date:', session.date, session.time);
        // console.log('Formatted date:', dateTime);

        return {
          id: session.id,
          title: session.title || session.session_type,
          start: dateTime,
          end: dateTime,
          desc: `${session.court || ""} - القاضي: ${
            session.judge || "غير محدد"
          } - ${session.notes || ""}`,
          type: session.session_type,
          color: getEventColor(session), // تمرير كامل object الجلسة
          time: session.time,
          rawSession: session,
          status: session.status, // إضافة حالة الجلسة
        };
      });

      setEvents(formattedEvents);
    } catch (error) {
      message.error("حدث خطأ في جلب المواعيد");
      logger.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedSession(event.rawSession);
    setViewSessionModalVisible(true);
  };

  // تحديث دالة handleEventDelete
  const handleEventDelete = async (eventId) => {
    try {
      await sessionsService.deleteSession(eventId);
      message.success("تم حذف الموعد بنجاح");
      await fetchEvents();
    } catch (error) {
      message.error("حدث خطأ أثناء حذف الموعد");
      logger.error("Error deleting event:", error);
    }
  };

  const getEventColor = (session) => {
    // تحديد اللون حسب حالة الجلسة
    const statusColors = {
      منعقدة: "#22c55e", // green
      قادمة: "#3b82f6", // blue
      مؤجلة: "#f97316", // orange
      ملغية: "#ef4444", // red
      مكتملة: "#22c55e", // green
      default: "#64748b", // gray
    };

    return statusColors[session.status] || statusColors.default;
  };

  // تحديث دالة eventStyleGetter
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color,
        borderRadius: "32px",
        opacity: event.status === "ملغية" ? 0.6 : 1,
        border: "none",
        color: "#ffffff",
        fontSize: "14px",
        padding: "2px 6px",
        margin: "2px 2px 2px 2px",
        cursor: "pointer",
      },
    };
  };

  // تحديث دالة onSelectSlot لحفظ التاريخ المحدد
  const handleSelectSlot = ({ start }) => {
    // تحويل التاريخ المحدد إلى التوقيت المحلي مع ضبط الوقت إلى 9 صباحاً
    const selectedMoment = moment(start)
      .local() // تحويل إلى التوقيت المحلي
      .startOf("day")
      .set({ hour: 19, minute: 0, second: 0 }); // تعيين بداية اليوم

    logger.debug("Selected date:", selectedMoment);

    setSelectedDate(selectedMoment);
    setShowAddModal(true);
  };

  // دالة معالجة التعديل
  const handleEdit = (session) => {
    setSessionToEdit(session);
    setViewSessionModalVisible(false);
    setIsEditModalVisible(true);
  };

  // دالة معالجة الحذف
  const handleDelete = async (sessionId) => {
    await fetchEvents(); // إعادة تحميل الأحداث بعد الحذف
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
        <div className="max-w-full mx-auto space-y-6">
          {/* Header */}
          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CalendarIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-800">التقويم</h1>
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  {/* إضافة مكون الإشعارات */}
                  <SessionNotifications events={events} />
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                    onClick={() => setShowAddModal(true)}
                  >
                    <Plus className="h-4 w-4 ml-2" />
                    إضافة موعد
                  </Button>
                  <Button variant="outline" className="border-gray-300">
                    <Filter className="h-4 w-4 ml-2" />
                    تصفية
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Main Content - Calendar and Events List */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar Section - Takes up 2/3 of the space */}
            <div className="lg:col-span-2">
              <Card className="border-none shadow-lg h-full">
                <CardContent className="p-6">
                  <Calendar
                    localizer={localizer}
                    events={events}
                    loading={loading}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: 700 }}
                    onSelectEvent={handleSelectEvent}
                    onSelectSlot={handleSelectSlot}
                    selectable={true}
                    popup
                    onView={(view) => setCurrentView(view)}
                    defaultView="month"
                    view={currentView}
                    rtl={true}
                    culture="ar"
                    formats={{
                      dateFormat: "D",
                      dayFormat: "D dddd",
                      monthHeaderFormat: "YYYY MMMM",
                      dayHeaderFormat: "dddd MM/DD",
                      dayRangeHeaderFormat: ({ start, end }) =>
                        `${moment(start).format("MM/DD")} - ${moment(
                          end
                        ).format("MM/DD")}`,
                    }}
                    eventPropGetter={eventStyleGetter}
                    dayPropGetter={(date) => ({
                      className: `calendar-day ${
                        moment(date).isSame(moment(), "day")
                          ? "current-day"
                          : ""
                      }`,
                      style: moment(date).isSame(moment(), "day")
                        ? {
                            position: "relative",
                          }
                        : {},
                    })}
                    className="custom-calendar rtl"
                    messages={{
                      next: "التالي",
                      previous: "السابق",
                      today: "اليوم",
                      month: "شهر",
                      week: "أسبوع",
                      day: "يوم",
                      agenda: "أجندة",
                      noEventsInRange: "لا توجد مواعيد في هذه الفترة",
                    }}
                    views={["month", "week", "day"]}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Events List Section - Takes up 1/3 of the space */}
            <div>
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-xl font-semibold">
                      المواعيد القادمة
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAddModal(true)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <Plus className="h-4 w-4 ml-1" />
                      إضافة
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {events
                    .filter((event) => new Date(event.start) >= new Date())
                    .sort((a, b) => new Date(a.start) - new Date(b.start))
                    .map((event) => (
                      <Card
                        key={event.id}
                        className="hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-100"
                        onClick={() => handleSelectEvent(event)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <Badge
                                variant={
                                  event.rawSession.status === "ملغية"
                                    ? "destructive"
                                    : event.rawSession.status === "مؤجلة"
                                    ? "warning"
                                    : event.rawSession.status === "منعقدة"
                                    ? "success"
                                    : event.rawSession.status === "مكتملة"
                                    ? "success"
                                    : "default"
                                }
                                className="text-xs font-medium px-2 py-1"
                              >
                                {event.rawSession.status || "قادمة"}
                              </Badge>
                            </div>
                            <div className="flex-grow min-w-0">
                              <h3 className="font-medium text-gray-800 truncate">
                                {event.title}
                              </h3>
                              <div className="flex items-center text-sm text-gray-500 mt-1">
                                <Clock className="h-3 w-3 ml-1" />
                                <span className="truncate">
                                  {moment(event.start).format("YYYY/MM/DD")} -{" "}
                                  {event.rawSession?.time ||
                                    moment(event.start).format("HH:mm")}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectEvent(event);
                                }}
                                className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  Modal.confirm({
                                    title: "تأكيد الحذف",
                                    content: "هل أنت متأكد من حذف هذا الموعد؟",
                                    okText: "نعم",
                                    cancelText: "لا",
                                    okButtonProps: { danger: true },
                                    onOk: () => handleEventDelete(event.id),
                                  });
                                }}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                  {events.filter((event) => new Date(event.start) >= new Date())
                    .length === 0 && (
                    <div className="text-center py-12">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                        <CalendarIcon className="h-8 w-8 text-gray-400" />
                      </div>
                      <p className="text-gray-500 font-medium">
                        لا توجد مواعيد قادمة
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        قم بإضافة موعد جديد للبدء
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Add Event Modal */}
        <AddSessionModal
          visible={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setSelectedDate(null);
          }}
          onSuccess={async () => {
            setShowAddModal(false);
            setSelectedDate(null);
            fetchEvents();
          }}
          defaultSessionType="جلسة قضائية"
          defaultDate={selectedDate} // تمرير التاريخ المحدد
        />

        {/* View Session Modal */}
        <ViewSessionModel
          visible={viewSessionModalVisible}
          onClose={() => setViewSessionModalVisible(false)}
          sessionData={selectedSession}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {/* Edit Session Modal */}
        <EditSessionModal
          visible={isEditModalVisible}
          onClose={() => {
            setIsEditModalVisible(false);
            setSessionToEdit(null);
          }}
          onSuccess={async () => {
            setIsEditModalVisible(false);
            setSessionToEdit(null);
            await fetchEvents();
            message.success("تم تحديث الجلسة بنجاح");
          }}
          sessionData={sessionToEdit}
        />

        <ToastContainer position="bottom-left" dir="rtl" />
      </div>
    </Layout>
  );
};

export default CalendarPage;
